<script setup lang="ts">
import { Step } from "@/types/editor/Wizard";
import { ref } from "vue";
import DocumentQuestionsQuestion from "./DocumentQuestionsQuestion.vue";

const { step, startingQuestionNumber = 1 } = defineProps<{
  step: Step;
  startingQuestionNumber?: number;
}>();

const subSteps = ref(step.subSteps);

const getQuestionNumber = (subStepIndex: number, questionIndex: number) => {
  let totalQuestions = 0;

  // Count all questions from previous subSteps
  for (let i = 0; i < subStepIndex; i++) {
    totalQuestions += subSteps.value[i].questions.length;
  }

  // Add current question index + starting question number - 1 (to account for the base)
  return startingQuestionNumber + totalQuestions + questionIndex;
};
</script>

<template>
  <div class="document-questions-step">
    <div class="step-header">
      <div class="step-icon">
        <v-icon size="24" color="white">mdi-help-circle-outline</v-icon>
      </div>
      <div class="step-content">
        <h2 class="step-title">{{ step.description }}</h2>
        <p class="step-subtitle">{{ $t("documentQuestions.stepSubtitle") }}</p>
      </div>
    </div>

    <div class="questions-content">
      <div v-for="(subStep, index) in subSteps" :key="subStep.id" class="sub-step">
        <DocumentQuestionsQuestion
          v-for="(question, qIndex) in subStep.questions"
          :key="question.id"
          :question="question"
          :question-number="getQuestionNumber(index, qIndex)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.document-questions-step {
  width: 100%;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;

  .step-icon {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .step-content {
    flex: 1;

    .step-title {
      font-family: "Archivo", sans-serif;
      font-weight: 600;
      font-size: 18px;
      line-height: 1.3;
      color: #1e293b;
      margin: 0 0 0.25rem 0;
      text-align: left;

      @media (max-width: 768px) {
        font-size: 16px;
      }
    }

    .step-subtitle {
      font-family: "Archivo", sans-serif;
      font-size: 14px;
      line-height: 1.4;
      color: #64748b;
      margin: 0;
      text-align: left;

      @media (max-width: 768px) {
        font-size: 13px;
      }
    }
  }
}

.questions-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.sub-step {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
